import { html, LitElement, css, unsafeCSS } from 'lit';
import { TooltipComponent } from '../components/tooltip';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';

import '../parts/card';
import '../parts/monster';
import '../parts/affix';
import '../components/decklist';
import '../components/discorduser';
import '../components/comments';

import style from './deck.scss';
import { cardExpansion, convertName, friendlyURL, getNameFromURL, getUserData, isSunforge, isTouchDevice, setTitle, trackPage } from '../shared/utils';
import { DATA_ADDED_BY_EVENT, DATA_AREAS, DATA_CLASSES } from '../data/sources';

export class DeckPage extends LitElement {

    static get properties() {
        return {
            deck: Object,
            error: String,
            timeline: Boolean
        }
    }

    constructor() {
        super();
        this.path = '';
        this.error = '';
        this.deck = {
            uid: '',
            name: '',
            class: '',
            diff: '',
            version: '',
            discorduser: '',
            stats: {
                trophy: '',
                fire: '',
                coin: '',
                clock1: ''
            },
            seed: '',
            slain: '',
            base: [],
            bosses: [],
            cards: [],
            imbue: [],
            talents: [],
            comments: ''
        };
        this.tooltip = new TooltipComponent();
        this.timeline = false;
        this.timer = null;
        this.userData = null;
    }

    static styles = css`
        ${unsafeCSS(style)}        
    `;

    connectedCallback() {
        super.connectedCallback();

        trackPage();

        this.path = getNameFromURL();

        this.loadUserData();
        
        if (this.path === 'create') {

            setTitle('Import Deck');
            return;
        }

        this.fetchData(this.path);
    }

    async loadUserData(){
        const user = await getUserData();
        if(user){
            this.userData = user;
            this.requestUpdate();
        }
    }

    async fetchData(deck) {
        if (deck.length < 15) {
            let res = await fetch(`./api/deck/${deck}`);
            if (res.status === 200) {
                this.deck = await res.json();
            } else {
                this.error = 'Error: Deck not found.';
            }
        } else {
            let res = await fetch(`./api/convertdeck/${deck}`);
            if (res.status === 200) {
                this.deck = await res.json();
            } else {
                this.error = 'Error: Deck creation failed.';
            }
        }
        if (this.deck) {
            setTitle('Deck | ' + this.deck.name + ' | ' + convertName(this.deck.class) + ' | ' + this.deck.diff);
        }
    }

    renderCard(card, talent) {
        if (card?.name?.indexOf('Card not found:') !== -1) {
            if (card?.name?.indexOf(' <9') !== -1) {
                let id = card?.name?.split(' <9')[0].replace('Card not found: ', '');
                return html`
                    <div class="row">
                        <card-part id=${id} talent=${talent}></card-part>                        
                    </div>                    
                 `;
            }
            return;
        }
        return html`
            <div class="row">
                <div @click=${() => { if (!isTouchDevice()) window.location = `./${talent ? 'talent' : 'card'}/${friendlyURL(card.name)}` }} 
                    @mouseleave=${this.tooltip.debouncer((ev) => this.tooltip.mouseOut(ev))} 
                    @mouseenter=${this.tooltip.debouncer((ev) => this.tooltip.mouseOver(ev, card.id))} class="artwork"
                    style="background-image:url(./images/icons/${(card.artwork || 'dt_portrait_bolgar').replace(/ /g, '%20')}.webp);">
                </div>
                <div @click=${() => { if (!isTouchDevice()) window.location = `./${talent ? 'talent' : 'card'}/${friendlyURL(card.name)}` }} class="name ${card.category === 10 ? 'talent' : 'card'}-rarity-${card.rarity}"
                        @mouseleave=${this.tooltip.debouncer((ev) => this.tooltip.mouseOut(ev))} 
                        @mouseenter=${this.tooltip.debouncer((ev) => this.tooltip.mouseOver(ev, card.id))}>
                        ${card.name}${card.upg ? html` <span style="color:#fff">${card.upg}</span>` : ''} ${card.qty ? html`<span style="color:#fff">x ${card.qty}</span>` : ''}
                </div>                                
                ${unsafeHTML(cardExpansion(card))}                            
            </div>
        `;
    }

    async createDeck() {

        let textboxes = this.shadowRoot.querySelectorAll('textarea');

        let val = textboxes[0].value;
        let comment = textboxes[1].value;

        this.error = '';

        val = val.replace(/\*\*/g, '*');
        val = val.replace(/----------------------------/g, '---');
        val = val.replace(':trophy: Total score: ', '$1');
        val = val.replace(':fire: Most damage: ', '$2');
        val = val.replace(':crown: Deck value: ', '$3');
        val = val.replace(':coin: Gold remaining: ', '$4');
        val = val.replace(':clock1: Duration: ', '$5');
        val = val.replace(':crossed_swords: ', '$6');
        val = val.replace(':rocket: ', '$7');
        val = val.replace(':game_die: ', '$8');
        val = val.replace('Bosses slain: ', '$9');

        'Weeping Mother,Garvin Greaves,Bolgar Blightbane,Spirit of Aethos,General Ral-Dagar,Serena Hellspark,Melchor the Cursed,Lord of Despair,Spine of Night'.split(',').forEach((boss, i) => {
            val = val.replace(boss, '$1' + i);
        })

        const encode = btoa(unescape(encodeURIComponent(val)));

        let res = await fetch('./api/createdeck', {
            method: 'POST',
            headers: {
                'Accept': '*/*',
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                deck: encode,
                comment
            })
        });

        let json = await res.json();
        if (json.id) {
            window.location = `./deck/${json.id}`;
        } else {
            this.error = 'Error: Deck creation failed. Please export the string from the game again.';
        }
    }

    statImage(name) {
        if (name === 'eye_spine')
            return html`<img loading="lazy" style="width:20px;height:20px;vertical-align:middle;" src="./images/monsters/dc_icon_spine.webp" alt="Spine of Night" />`;
        if (name === 'trophy')
            return '🏆';
        if (name === 'fire')
            return '🔥';
        if (name === 'crown')
            return '👑';
        if (name === 'coin')
            return '💰';
        if (name === 'clock1')
            return '🕐';
    }

    toggleTimeline() {

        if (this.timer) {
            window.clearInterval(this.timer);
            this.timer = null;
        } else {
            const cont = this.shadowRoot.getElementById('timeline-container');
            const contwidth = cont.scrollWidth;
            this.timer = window.setInterval(() => {
                if (cont.scrollLeft !== contwidth) {
                    cont.scrollTo(cont.scrollLeft + 1, 0);
                }
            }, 15);
        }

    }

    showTimeline() {

        window.clearInterval(this.timer);
        this.timer = null;

        window.setTimeout(() => this.toggleTimeline(), 500);

        // Sunforge: wep, attr, attr, tal, card, card, card, tal, card, card, card, tal, card, card, card, tal, card, card, attr
        const sunforge = isSunforge(this.deck.class);
        let timeline = [];
        let areas = [];
        [...this.deck.bosses, this.deck.slain]?.forEach((b, i) => {
            if (b) {
                if (b === 'Guild Assassin')
                    b = 'Semira';
                if (b?.startsWith('Priest of'))
                    b = b.replace('Priest', 'High Priest');
                if (b === 'Xhev\'Araxx')
                    b = 'Outerworldly Entity';
                let area = DATA_AREAS.filter(a => a.boss === b || a.miniBosses?.indexOf(b) !== -1 || a.areaEnemies?.indexOf(b) !== -1);
                let areaname = area[0]?.name
                if (!sunforge) {
                    if (b === 'Semira')
                        areaname = '3 The House Vesparin';
                    if (b === 'Dawnbringer Solspear')
                        areaname = '6 Temple of the Pyre';
                    if (b === 'Viola Skysworn' || b === 'Elder Lich')
                        areaname = '6 The Ambermines';
                } else {
                    areaname = null;
                }
                timeline.push({
                    type: 'monster',
                    id: b,
                    area: areaname,
                    slain: this.deck.slain === b,
                    sort: sunforge ? (i + 1) * 10 : 0
                });
                if (!sunforge && areas.indexOf(areaname) === -1)
                    areas.push(areaname);
            }
        });
        let level = 1;
        this.deck.talents?.forEach((t, i) => {
            let fromevent = !sunforge ? DATA_ADDED_BY_EVENT.filter(added => added.card === t.id) : [];
            let srt = 0;
            if (sunforge) {
                if (i < 4) {
                    srt = i + 1;
                } else {
                    srt = i * 200;
                }
            }
            timeline.push({
                type: 'talent',
                id: t.id,
                fromevent: fromevent[0]?.event,
                sort: srt
            });
            if (i > 0 && fromevent.length === 0)
                level += 1;
        });

        let run = [];
        let maxarea = 0;
        areas.forEach((a, i) => {
            run.push({
                type: 'area',
                id: a,
                sort: i * 100
            });
            if (a.indexOf('9') === -1 && a.indexOf('10') === -1)
                maxarea += 1;
        });

        if (!sunforge) {
            for (let l = 1; l <= level; l++) {
                let lsort = l === 1 ? 1 : Math.floor((l - 1) / level * (maxarea * 100));
                if(lsort % 100 === 0)
                    lsort += 10;
                run.push({
                    type: 'level',
                    id: l,
                    sort: lsort
                });
            }
        } else {
            run.push({
                type: 'level',
                id: 1,
                sort: 0
            });
        }

        let l = 1;
        let nextsrt = null;
        timeline.forEach(t => {
            if (t.type === 'monster') {
                run.push({
                    type: 'monster',
                    id: t.id,
                    sort: sunforge ? t.sort : run.filter(r => r.id === t.area)[0]?.sort + 100 - 5,
                    slain: t.slain
                });
                if (sunforge) {
                    if (t.sort === 10) {
                        run.push({
                            type: 'portal',
                            id: '1 (Normal)',
                            sort: t.sort - 1
                        });
                    }
                    if (t.sort === 90) {
                        run.push({
                            type: 'portal',
                            id: '2 (Challenging)',
                            sort: t.sort - 1
                        });
                    }
                    if (t.sort === 170) {
                        run.push({
                            type: 'portal',
                            id: '3 (Hard)',
                            sort: t.sort - 1
                        });
                    }
                    if (t.sort === 250) {
                        run.push({
                            type: 'portal',
                            id: '4 (Impossible)',
                            sort: t.sort - 1
                        });
                    }
                }
            }
            if (t.type === 'talent') {
                if (!t.fromevent) {
                    nextsrt = null;
                    run.push({
                        type: 'talent',
                        id: t.id,
                        sort: sunforge ? t.sort : (run.filter(r => r.id === l && r.type === 'level')[0]?.sort || 1) + 1
                    });
                    l += 1;
                } else {
                    let srt = nextsrt || run[run.length - 1].sort + 1;
                    nextsrt = null;
                    let srt2 = srt + 1;
                    if (t.fromevent === 'Suspended Cage') {
                        srt = run.filter(r => r.id === '6 Temple of the Pyre')[0]?.sort + 100 - 50;
                        srt2 = srt + 1;
                    }
                    if (t.fromevent === 'Grove of the Dying Star  Finish') {
                        srt = run.filter(r => r.id === 'Spirit of Aethos')[0]?.sort + 1;
                        srt2 = srt + 1;
                        nextsrt = (run.filter(r => r.sort > srt2 && r.type === 'area')[0]?.sort || srt2) + 1;
                    }
                    if (t.fromevent === 'The Deep Finish') {
                        srt = run.filter(r => r.id === 'Melchor the Cursed')[0]?.sort + 1;
                        if(isNaN(srt)){
                            srt = run.filter(r => r.id === '7 The Godscar Wastes')[0]?.sort + 1;
                            srt2 = srt + 1;
                            t.fromevent = 'The Godscar Wastes Start';                            
                        }else{
                            srt2 = srt + 1;
                            nextsrt = (run.filter(r => r.sort > srt2 && r.type === 'area')[0]?.sort || srt2) + 10;      
                        }                                                                      
                    }
                    if (t.fromevent === 'Serena Hellspark') {
                        srt = run.filter(r => r.id === 'Viola Skysworn' || r.id === 'Serena Hellspark')[0]?.sort - 2
                        srt2 = srt + 2;
                        if (t.id === 77411)
                            srt2 = srt + 1;
                        nextsrt = (run.filter(r => r.sort > srt2 && r.type === 'area')[0]?.sort || srt2) + 1;
                    }
                    let skipev = sunforge;
                    if (t.id === 77411 && timeline.filter(t2 => t2.id === 334396).length > 0)
                        skipev = true;
                    if (!skipev)
                        run.push({
                            type: 'event',
                            id: t.fromevent,
                            sort: srt
                        });
                    run.push({
                        type: 'talent',
                        id: t.id,
                        sort: srt2
                    });
                }
            }
        });

        run.sort((a, b) => {
            return a.sort - b.sort;
        });

        console.log(run);

        if (!sunforge) {
            run[0].sort = 2;
            run[1].sort = 0;
            run[2].sort = 1;
            run.sort((a, b) => {
                return a.sort - b.sort;
            });
        }

        const cls = DATA_CLASSES.filter(c => this.deck?.class?.indexOf(c.name) !== -1)[0];

        return html`
        <h3>Timeline (${run.length})</h3>  
        <div id="timeline-container" style="width:100%;overflow-y: hidden;overflow-x: auto;height:120px;">
            <div style="width: 10000px;height:90px;">
                ${run.map((r, i) => {
            if (r.type === 'monster')
                return html`<div style="position:relative;display:inline-block;"><monster-part id=${r.id} class="timeline"></monster-part><small class="timeline-desc" style="top:10px;right:10px;${r.slain ? 'color:#eb4b4b' : ''}">${r.slain ? 'Slain By' : 'Killed Boss'}</small></div>`;
            if (r.type === 'talent')
                return html`<div class="bubble big timeline"><card-part id=${r.id} talent="true"></card-part><small class="timeline-desc">${i === 1 && !sunforge ? 'Weapon Power' : 'Added Talent'}</small></div>`;
            if (r.type === 'event')
                return html`<div class="bubble big timeline"><event-part id=${r.id}></event-part><small class="timeline-desc">Event</small></div>`;
            if (r.type === 'area')
                return html`&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;<div class="bubble big timeline"><img loading="lazy" class="world" src="./images/card_world_${+r.id.substr(0, 2)}.webp" alt=${r.id} /> <a href='./area/${friendlyURL(r.id)}'>${r.id.replace('<br>', ' ')}</a><small class="timeline-desc">Canto</small></div>`;
            if (r.type === 'level')
                return html`<div class="bubble big timeline levelup">${sunforge ? html`<img loading="lazy" class="class-image" src='./images/icons/cardart_5_50.webp' alt="Sunforge Run" /> ` : html`<img loading="lazy" class="class-image" src='./images/classes/${cls.artwork}.webp' alt=${cls.name} />`} Level ${r.id}<small class="timeline-desc">Character Level</small></div>`;
            if (r.type === 'portal')
                return html`&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;<div class="bubble big timeline levelup"><img loading="lazy" class="class-image" src="./images/icons/cards_metamorphosis_58.webp" alt=${r.id} /> Portal ${r.id}<small class="timeline-desc">Portal</small></div>`;
        })}
            </div>
        </div>        
        <div class="button small" @click=${this.toggleTimeline}>Play/Pause</div> <div class="button small" @click=${()=>this.timeline=false}>Close Timeline</div>
        <br>
        <small style="color:gray">Note: Timeline view is an <b>estimate</b> of events based on information contained in the deck snippet code.</small>        
        <br><br>
        `
    }

    render() {

        if (this.path === 'create')
            return html`
                ${this.error ? html`<p style="color:red">${this.error}</p>` : ''}
                <h1>Import Deck</h1>
                ${this.userData ? html`
                <p>Note: Please add comments to the second box so it is easier to verify the deck string</p>
                <textarea placeholder="Import Deck String (from Game)" rows="20"></textarea>
                <textarea placeholder="Comments (optional)" rows="10"></textarea>
                <div class="button" @click=${(ev) => this.createDeck()}>Create</div>
                `: html`
                <p>You need to be logged in to import a deck.</p>
                <p><a class="button" href="./auth/login">Login</a></p>
                `}
                <decklist-component url="recentdecks"></decklist-component>   
            `;

        if (!this.deck.name)
            return html``;

        if(this.deck.slain === ' ')
            this.deck.slain = '';

        if (this.deck.talents)
            this.deck.talents = this.deck.talents.filter(card => card?.name?.indexOf('Card not found:') === -1);

        if (this.deck.talents?.length > 0)
            this.deck.base.push(this.deck.talents[0]);

        let artwork = 'cardart_5_50';
        if (this.deck.talents[0])
            artwork = this.deck.talents[0].artwork;
        if (this.deck.base[1])
            artwork = this.deck.base[1].artwork;

        const cls = DATA_CLASSES.filter(c => this.deck?.class?.indexOf(c.name) !== -1)[0];

        let cardcount = 0;
        this.deck.cards.forEach(d => {
            if (d?.name?.indexOf('Card not found:') === -1 || d?.name?.indexOf(' <9') !== -1)
                cardcount += 1 * (d.qty || 1)
        });        

        return html`                   
            ${this.tooltip}                       
            <h1>
                <div class="header-image" style="background-image:url(./images/icons/${(artwork || '').replace(/ /g, '%20')}.webp);"></div> ${this.deck.name}
            </h1>                                         
            ${this.deck.uid ? html`
                <p style="color:#CCC;font-size:14px;">
                    Posted: ${new Date(this.deck.uid).toLocaleString()} ${this.deck.discorduser ? html`by <discorduser-component user=${this.deck.discorduser}></discorduser-component>` : ''}
                </p>
            `: ''}  
            ${this.timeline ? this.showTimeline() : ''}     
            ${!isSunforge(this.deck.class) && !this.timeline && this.deck.bosses?.length > 0 ? html`<div class="button" style="${!isTouchDevice() ? 'position:absolute;top:20px;right:20px;' : 'display:block;width:150px;'}" @click=${() => this.timeline = !this.timeline}>Timeline <small>(beta)</small></div>  ` : ''}  
            <h3>Summary</h3>                                  
            <div class="bubble big class-${(this.deck.subclass || cls?.name || 'scion').toLowerCase()}" style="font-weight:bold;">
                ${cls ? html`
                    <img loading="lazy" class="class-image" src='./images/classes/${cls.artwork}.webp' alt=${cls.name} />          
                `: html`
                    <img loading="lazy" class="class-image" src='./images/icons/cardart_5_50.webp' alt="Sunforge Run" />     
                `}   
                ${convertName(this.deck.class) + ' ' + (isSunforge(this.deck.class) ? `[${Math.round(this.deck.bosses.length / 32 * 100)}% complete]` : this.deck.diff)}  
            </div>   
            ${this.deck.challenge ? html`
                <div class="bubble big">
                ⚔️ <a href="./challenge/${this.deck.challenge}">Challenge Submission</a>
                </div>
            `: ''}        
            ${isTouchDevice() ? html`<br>` : ''}             
            <div class="bubble big">
                🎲 <a href="./seed/${this.deck.seed}">${this.deck.seed}</a>
            </div>
            ${isTouchDevice() ? html`<br>` : ''}
            ${Object.keys(this.deck.stats).map((stat) => {
            if (this.deck.stats[stat] !== '')
                if (stat === 'trophy') {
                    let score = this.deck.stats.trophy?.replace(/[^-?\d]/g, '') || '0';
                    return html`
                        <div class="bubble big">
                            ${this.statImage(stat)} Total score: ${score}${this.deck.bonus ? html` <span style="color:${this.deck.bonus > 0 ? 'lightgreen' : '#ffcccc'};font-size:12px;font-weight:bold;">${this.deck.bonus > 0 ? '+' : ''}${this.deck.bonus}</span>` : ''}
                        </div>${isTouchDevice() ? html`<br>` : ''}
                        `;
                } else {
                    return html`
                        <div class="bubble big">
                            ${this.statImage(stat)} ${unsafeHTML(this.deck.stats[stat].replace('**', '<b>').replace('**', '</b>').replace('*', '<b>').replace('*', '</b>'))}
                        </div>${isTouchDevice() ? html`<br>` : ''}
                        `;
                }
        })}            
            ${this.deck.version ? html`
                <div class="bubble big">
                    <img loading="lazy" style="width:25px;height:25px;vertical-align:middle;" src="./images/monsters/dc_dantelion.webp" alt="Version" /> Version: <b>${this.deck.version}</b>
                </div>`: ''}
            ${this.deck.bonuses?.length > 0 ? html`      
            <h3>Challenge Bonuses</h3>         
            ${this.deck.bonuses.map(b => html`
                <div class="bubble big" style="color:${b.indexOf('-') === -1 ? 'lightgreen' : '#ffcccc'}">${b.indexOf('-') === -1 && b.indexOf('x') === -1 ? '+' + b : b}</div>
            `)}            
            `: ''}
            <h3>Bosses slain (${this.deck.bosses.length})</h3>
            ${this.deck.bosses.filter(monster => monster !== 'Guild Assassin').map((boss, i) => {
            return html`
                    ${i === 0 && isSunforge(this.deck.class) ? html`<h4>Portal 1 (Normal)</h4>` : ''}
                    ${i === 8 && isSunforge(this.deck.class) ? html`<h4>Portal 2 (Challenging)</h4>` : ''}
                    ${i === 16 && isSunforge(this.deck.class) ? html`<h4>Portal 3 (Hard)</h4>` : ''}
                    ${i === 24 && isSunforge(this.deck.class) ? html`<h4>Portal 4 (Impossible)</h4>` : ''}
                    <monster-part id="${boss}"></monster-part>${isTouchDevice() ? html`<br>` : ''}
                `
        })}
            ${this.deck.slain ? html`
                ${!isTouchDevice() ? html`<br>` : ''}  
                <h3>Slain by</h3>
                <monster-part id="${this.deck.slain}"></monster-part>                
            `: ''}
            <br>
            <br>
            ${this.deck.base.map((card,i) => {
                if(i < 3)
                return this.renderCard(card);
        })}
            ${this.deck.malignances && this.deck.malignances?.length > 0 ? html`
                <h3>Malignancies (${this.deck.malignances.map(mal => +mal.perc).reduce((a, b) => a + b)}%)</h3>
                ${this.deck.malignances.map((mal) => html`
                <affix-part id=${mal.name}></affix-part> (${mal.perc}%)<br>
                `)}
            `: ''}
            <h3>Decklist (${cardcount})</h3>
            ${this.deck.cards.map((card) => {
            return this.renderCard(card);
        })}
            <h3>Imbues (${this.deck.imbue.length})</h3>
            ${this.deck.imbue.map((card) => {
            return this.renderCard(card);
        })}
            <h3>Talents (${this.deck.talents.length})</h3>
            ${this.deck.talents.map((card) => {
            return this.renderCard(card, true);
        })}
            <comments-component path=${window.location.pathname}></comments-component>
        `;
    }
}

customElements.define('deck-page', DeckPage);